import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Refresh, FilterList, Search, Engineering } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  TypePill,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Shared Service Work Order types — re-exported for the Form/Detail pages,
// the same way PurchaseRequisitionPage shares its status types with its
// Form page. Keeps the single source of truth in the list page.
// ---------------------------------------------------------------------------

export type SwoStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export const SWO_STATUSES: SwoStatus[] = ['DRAFT', 'SUBMITTED', 'APPROVED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];

export const SWO_STATUS_TONE: Record<SwoStatus, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  APPROVED: 'success',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};

export interface SwoRow {
  id: number;
  swo_number: string;
  vendor_id: number | string;
  vendor_name: string;
  service_category: string;
  wo_date: string;
  estimated_value: number | string;
  total_value: number | string;
  status: SwoStatus;
}

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const ServiceWorkOrderPage: React.FC = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState<SwoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<SwoStatus | ''>('');
  const [search, setSearch] = useState('');

  const fetchOrders = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ serviceWorkOrders: SwoRow[]; count: number }>('/service-work-orders', {
        params: { status: statusFilter || undefined },
      })
      .then((r) => setOrders(r.serviceWorkOrders || []))
      .catch((err: any) => setError(err.message || 'Failed to load service work orders'))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(
      (o) =>
        o.swo_number?.toLowerCase().includes(q) ||
        o.vendor_name?.toLowerCase().includes(q) ||
        o.service_category?.toLowerCase().includes(q)
    );
  }, [orders, search]);

  const stats = useMemo(() => {
    const draft = orders.filter((o) => o.status === 'DRAFT').length;
    const approved = orders.filter((o) => o.status === 'APPROVED').length;
    const inProgress = orders.filter((o) => o.status === 'IN_PROGRESS').length;
    const completed = orders.filter((o) => o.status === 'COMPLETED').length;
    const totalValue = orders.reduce((sum, o) => sum + toNum(o.total_value), 0);
    return { draft, approved, inProgress, completed, totalValue };
  }, [orders]);

  const hasFilters = !!(statusFilter || search);
  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Service Work Orders"
        subtitle="Commission and track outside services before they can be entered and billed"
        actions={
          <>
            <IconButton onClick={fetchOrders} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<Add />}
              size="small"
              onClick={() => navigate('/procurement/service-work-orders/new')}
            >
              New Work Order
            </Button>
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Draft" value={stats.draft} icon={<Engineering />} color="#64748b" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Approved" value={stats.approved} icon={<Engineering />} color="#059669" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="In Progress" value={stats.inProgress} icon={<Engineering />} color="#d97706" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Completed" value={stats.completed} icon={<Engineering />} color="#0284c7" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Total Value" value={formatIndianNumber(stats.totalValue)} icon={<Engineering />} color="#7c3aed" />
        </Grid>
      </Grid>

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search SWO, vendor or category"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as SwoStatus | '')}
          sx={{ minWidth: 170 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {SWO_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace('_', ' ')}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['SWO', 'Vendor', 'Category', 'WO Date', 'Status', 'Total Value'].map((h, i) => (
                  <TableCell key={h} align={i === 5 ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No service work orders match these filters"
                      hint="Clear the filters, or raise a new work order for an outside service."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((o) => (
                  <TableRow
                    key={o.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/procurement/service-work-orders/${o.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={o.swo_number} tint="#0284c7" icon={<Engineering fontSize="small" />} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {o.vendor_name}
                    </TableCell>
                    <TableCell>
                      <TypePill label={o.service_category || 'General'} tint="#7c3aed" />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(o.wo_date)}</TableCell>
                    <TableCell>
                      <StatusDot label={o.status.replace('_', ' ')} tone={SWO_STATUS_TONE[o.status] || 'neutral'} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {formatIndianNumber(toNum(o.total_value))}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default ServiceWorkOrderPage;
